import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronLeft } from 'lucide-react';
import InviteAppGate from '../../components/InviteAppGate';
import {
  storePendingStaffInviteToken,
  consumePendingStaffInviteToken,
} from '../../utils/deepLinkHandler';
import { markLaunchDeepLinkConsumed } from '../../utils/nativeDeepLinks';
import { getRoleLandingPath } from '../../utils/roleSwitchFlow';
import { useBackTarget } from '../../hooks/useBackTarget';
import { trackInviteViewed, trackInviteAccepted } from '../../utils/airbridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const font = "'Inria Serif', serif";

const cardStyle = {
  width: '100%',
  background: '#FFFFFF',
  border: '1px solid rgba(112, 63, 246, 0.10)',
  boxShadow: '0px 4px 4px 0px rgba(112, 63, 246, 0.18)',
  borderRadius: 22,
  padding: '22px 20px',
};

export default function StaffInviteEntryPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const { token } = useParams();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const authToken = localStorage.getItem('token');
  const isAuthed = Boolean(currentUser && authToken);

  useEffect(() => {
    if (token) {
      storePendingStaffInviteToken(token);
    }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/staff-invite/${token}`);
        setInvite(res.data);
        // Airbridge: invite_viewed fires once we know the invite actually
        // resolved (status 200). Skip on 404/expired so funnel numbers stay
        // clean. `inviteId` = the token, matching the create/share events
        // fired on the owner / admin side.
        trackInviteViewed({
          inviteId: token,
          inviteType: 'venue_staff',
          extra: {
            venue_app_id: res.data?.venue_app_id || '',
            status: res.data?.status || '',
          },
        });
      } catch (err) {
        setError(err.response?.data?.detail || 'Invite link not found');
      } finally {
        setLoading(false);
      }
    };
    if (token) void load();
  }, [token]);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/staff-invites/${token}/accept`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      // Airbridge: closes the venue_staff funnel — paired with `invite_viewed`
      // (above) and the upstream `airbridge.user.signup` event fired in
      // RegisterPage when the recipient signed up to redeem this invite.
      trackInviteAccepted({
        inviteId: token,
        inviteType: 'venue_staff',
        extra: {
          venue_app_id: res.data?.venue_app_id || '',
          staff_role: res.data?.staff_role || 'staff',
        },
      });
      consumePendingStaffInviteToken();
      // Prevent Capacitor's launch intent from bouncing the user back
      // onto this screen after the window.location.replace() below. See
      // nativeDeepLinks.js → CONSUMED_LAUNCH_LINKS_SS_KEY.
      try {
        markLaunchDeepLinkConsumed(`/staff-invite/${token}`);
      } catch { /* noop — sessionStorage unavailable */ }
      try {
        const sw = await axios.post(
          `${API}/auth/switch-role`,
          { target_role: 'venue_member' },
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        localStorage.setItem('token', sw.data.access_token);
        localStorage.setItem('user', JSON.stringify(sw.data.user));
      } catch (_err) { /* best effort */ }
      let landingPath = getRoleLandingPath('venue_member');
      try {
        const roleRes = await axios.get(`${API}/venue/staff/my-role`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (roleRes.data?.role === 'admin') landingPath = '/staff-admin/scan';
      } catch (_err) { /* fall back */ }
      toast.success(`Welcome to ${res.data.venue_name || 'your venue'}`);
      window.location.replace(landingPath);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unable to accept invite');
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    consumePendingStaffInviteToken();
    if (isAuthed) {
      navigate(getRoleLandingPath(currentUser?.role || 'guest'), { replace: true });
    } else {
      navigate('/', { replace: true });
    }
  };

  const venueName = invite?.venue_name || 'a venue';
  const venuePhoto = invite?.venue_photo || '';
  const inviterName = invite?.inviter_first_name;
  const invitedFull = invite?.invited_full_name || invite?.invited_first_name || '';
  const invitedFirst = invite?.invited_first_name;
  const welcomeHeadline = invitedFull
    ? `Welcome, ${invitedFull}`
    : (inviterName ? `${inviterName} invited you` : "You're invited");
  const inviterSubLine = inviterName
    ? `You were invited by ${inviterName} from ${venueName}`
    : `You were invited to ${venueName}`;
  const gateHeadline = inviterName
    ? `${inviterName} invited you to ${venueName}`
    : `You're invited to ${venueName}`;

  const initials = (invitedFirst || '').slice(0, 2).toUpperCase();

  return (
    <InviteAppGate invitedToLabel={gateHeadline} invitedSubLabel="">
      <div
        className="app-shell"
        data-testid="staff-invite-entry-page"
        style={{
          backgroundColor: '#FFFFFF',
          padding: '0 22px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <button
          data-testid="staff-invite-entry-back-button"
          onClick={goBack}
          style={{
            position: 'absolute',
            top: 18,
            left: 18,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
          }}
        >
          <ChevronLeft size={34} color="#000" strokeWidth={1.6} />
        </button>

        {/* Avatar + welcome */}
        {invitedFirst && !error ? (
          <div
            data-testid="staff-invite-entry-avatar"
            style={{
              marginTop: 76,
              width: 92,
              height: 92,
              borderRadius: '50%',
              backgroundColor: '#E8D5B7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 28px rgba(112, 63, 246, 0.20)',
              border: '3px solid #fff',
            }}
          >
            <span style={{ fontFamily: font, fontSize: 36, fontWeight: 700, color: '#5f4b2d' }}>
              {initials}
            </span>
          </div>
        ) : (
          <img
            src="/allure-logo.png"
            alt="Allure"
            style={{ width: 132, height: 118, objectFit: 'contain', marginTop: 56 }}
          />
        )}

        <h1
          data-testid="staff-invite-entry-heading"
          style={{
            fontFamily: font,
            fontSize: 28,
            fontWeight: 700,
            color: '#000',
            textAlign: 'center',
            margin: '18px 0 6px',
            lineHeight: 1.1,
            maxWidth: 340,
          }}
        >
          {error ? 'Invite unavailable' : welcomeHeadline}
        </h1>

        {!error && invite ? (
          <p
            data-testid="staff-invite-entry-inviter"
            style={{
              fontFamily: font,
              fontSize: 17,
              color: '#555',
              textAlign: 'center',
              margin: 0,
              lineHeight: 1.35,
              maxWidth: 340,
            }}
          >
            {inviterSubLine}
          </p>
        ) : null}

        {loading ? (
          <p style={{ fontFamily: font, fontSize: 18, color: '#666', marginTop: 22 }}>
            Loading invite…
          </p>
        ) : null}

        {error ? (
          <p
            data-testid="staff-invite-entry-error"
            style={{ fontFamily: font, fontSize: 18, color: '#E53935', marginTop: 22, textAlign: 'center' }}
          >
            {error}
          </p>
        ) : null}

        {invite && !error ? (
          <div data-testid="staff-invite-entry-card" style={{ ...cardStyle, marginTop: 24 }}>
            {!isAuthed ? (
              <>
                <p
                  style={{
                    fontFamily: font,
                    fontSize: 17,
                    color: '#000',
                    margin: '0 0 16px',
                    lineHeight: 1.35,
                    textAlign: 'center',
                  }}
                >
                  Create your Allure account to accept this staff invite. We&apos;ll bring you right back here once you&apos;re in.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    data-testid="staff-invite-entry-register-button"
                    onClick={() => navigate('/register')}
                    style={{
                      padding: '14px 18px',
                      borderRadius: 9999,
                      border: 'none',
                      background: '#04A628',
                      color: '#FFFFFF',
                      fontFamily: font,
                      fontSize: 21,
                      fontWeight: 700,
                      cursor: 'pointer',
                      boxShadow: '0 10px 24px rgba(4, 166, 40, 0.22)',
                    }}
                  >
                    Create account
                  </button>
                  <button
                    data-testid="staff-invite-entry-login-button"
                    onClick={() => navigate('/login')}
                    style={{
                      padding: '12px 18px',
                      borderRadius: 9999,
                      border: '1px solid rgba(0,0,0,0.12)',
                      background: '#FFFFFF',
                      color: '#000',
                      fontFamily: font,
                      fontSize: 18,
                      cursor: 'pointer',
                    }}
                  >
                    I already have an account
                  </button>
                </div>
              </>
            ) : (
              <>
                {venuePhoto ? (
                  <div
                    data-testid="staff-invite-entry-venue-photo"
                    style={{
                      width: '100%',
                      aspectRatio: '16 / 9',
                      borderRadius: 16,
                      overflow: 'hidden',
                      background: '#F5F1FF',
                      marginBottom: 14,
                      boxShadow: '0 6px 16px rgba(112, 63, 246, 0.12)',
                    }}
                  >
                    <img
                      src={venuePhoto}
                      alt={venueName}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ) : null}
                <p
                  style={{
                    fontFamily: font,
                    fontSize: 17,
                    color: '#000',
                    margin: '0 0 16px',
                    lineHeight: 1.35,
                    textAlign: 'center',
                  }}
                >
                  Tap accept to join {venueName} as a staff member.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    data-testid="staff-invite-entry-accept-button"
                    onClick={handleAccept}
                    disabled={submitting}
                    style={{
                      padding: '14px 18px',
                      borderRadius: 9999,
                      border: 'none',
                      background: '#04A628',
                      color: '#FFFFFF',
                      fontFamily: font,
                      fontSize: 21,
                      fontWeight: 700,
                      cursor: submitting ? 'default' : 'pointer',
                      opacity: submitting ? 0.6 : 1,
                      boxShadow: '0 10px 24px rgba(4, 166, 40, 0.22)',
                    }}
                  >
                    {submitting ? 'Joining…' : 'Accept'}
                  </button>
                  <button
                    data-testid="staff-invite-entry-decline-button"
                    onClick={handleDecline}
                    style={{
                      padding: '12px 18px',
                      borderRadius: 9999,
                      border: '1px solid rgba(229, 57, 53, 0.4)',
                      background: '#FFFFFF',
                      color: '#E53935',
                      fontFamily: font,
                      fontSize: 18,
                      cursor: 'pointer',
                    }}
                  >
                    Decline
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>
    </InviteAppGate>
  );
}
